import React, { useEffect, useState } from 'react';
import Logo from './Logo';

interface AppLoaderProps {
  isVisible: boolean;
  onLoadingComplete?: () => void;
}

const AppLoader: React.FC<AppLoaderProps> = ({ isVisible, onLoadingComplete }) => {
  const [loadingPhase, setLoadingPhase] = useState<'initializing' | 'loading' | 'complete'>('initializing');
  const [loadingText, setLoadingText] = useState('Initializing System...');
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (!isVisible) return;

    const phases = [
      { phase: 'initializing', text: 'Initializing System', duration: 1000 },
      { phase: 'loading', text: 'Loading Components', duration: 1500 },
      { phase: 'complete', text: 'Ready to Launch', duration: 800 }
    ];

    let currentPhaseIndex = 0;
    let progressInterval: NodeJS.Timeout;
    let phaseTimeout: NodeJS.Timeout;
    let dotInterval: NodeJS.Timeout;

    // Animate dots
    const animateDots = () => {
      let dotCount = 0;
      dotInterval = setInterval(() => {
        dotCount = (dotCount + 1) % 4;
        setDots('.'.repeat(dotCount));
      }, 400);
    };

    const startProgressAnimation = () => {
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + Math.random() * 12;
        });
      }, 120);
    };

    const nextPhase = () => {
      if (currentPhaseIndex < phases.length) {
        const currentPhase = phases[currentPhaseIndex];
        setLoadingPhase(currentPhase.phase as any);
        setLoadingText(currentPhase.text);

        if (currentPhase.phase === 'loading') {
          startProgressAnimation();
        }

        if (currentPhase.phase !== 'complete') {
          animateDots();
        } else {
          clearInterval(dotInterval);
          setDots('');
        }

        phaseTimeout = setTimeout(() => {
          currentPhaseIndex++;
          if (currentPhaseIndex < phases.length) {
            nextPhase();
          } else {
            setTimeout(() => {
              onLoadingComplete?.();
            }, 400);
          }
        }, currentPhase.duration);
      }
    };

    nextPhase();

    return () => {
      clearInterval(progressInterval);
      clearInterval(dotInterval);
      clearTimeout(phaseTimeout);
    };
  }, [isVisible, onLoadingComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-green-900 to-emerald-900 z-50 overflow-hidden">
      {/* Dynamic Background with Particles */}
      <div className="absolute inset-0">
        {/* Animated Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            animation: 'gridMove 20s linear infinite'
          }}
        ></div>
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-green-500/20 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-emerald-400/20 rounded-full blur-xl animate-bounce" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-16 w-40 h-40 bg-green-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-20 w-28 h-28 bg-emerald-500/20 rounded-full blur-2xl animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        
        {/* Geometric Patterns */}
        <div className="absolute top-1/4 left-10 w-2 h-20 bg-gradient-to-b from-green-400 to-transparent opacity-30 animate-pulse"></div>
        <div className="absolute top-1/3 right-12 w-20 h-2 bg-gradient-to-r from-emerald-400 to-transparent opacity-30 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute bottom-1/4 left-16 w-2 h-16 bg-gradient-to-t from-green-500 to-transparent opacity-25 animate-pulse" style={{ animationDelay: '2.5s' }}></div>
      </div>

      {/* Main Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          
          {/* Premium Card Container */}
          <div className={`bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 text-center transform transition-all duration-1000 ${
            loadingPhase === 'initializing' ? 'scale-95 opacity-80' : 
            loadingPhase === 'loading' ? 'scale-100 opacity-95' : 'scale-105 opacity-100'
          }`}>
            
            {/* Logo Section with Advanced Animation */}
            <div className="relative mb-8">
              {/* Hexagon Background */}
              <div className="relative flex justify-center items-center">
                {/* Outer Hexagon Ring */}
                <div className={`absolute w-32 h-32 transition-all duration-1000 ${
                  loadingPhase === 'complete' ? 'opacity-0 scale-110' : 'opacity-60'
                }`}>
                  <svg className="w-full h-full animate-spin" style={{ animationDuration: '8s' }} viewBox="0 0 100 100">
                    <polygon 
                      points="30,75 70,75 90,50 70,25 30,25 10,50" 
                      fill="none" 
                      stroke="url(#gradient1)" 
                      strokeWidth="2"
                      strokeDasharray="10,5"
                    />
                    <defs>
                      <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10B981" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                
                {/* Inner Rotating Circle */}
                <div className={`absolute w-24 h-24 border-2 border-transparent border-t-emerald-400 border-r-green-400 rounded-full transition-all duration-500 ${
                  loadingPhase === 'complete' ? 'opacity-0 scale-95' : 'opacity-80 animate-spin'
                }`} style={{ animationDuration: '2s' }}></div>
                
                {/* Logo with Glow Effect */}
                <div className={`relative z-20 transform transition-all duration-1000 ${
                  loadingPhase === 'initializing' ? 'scale-90 opacity-70' : 
                  loadingPhase === 'loading' ? 'scale-100 opacity-90' : 'scale-110 opacity-100'
                }`}>
                  <div className="p-4 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-300/30">
                    <Logo size="xl" showText={false} className="filter drop-shadow-lg" />
                  </div>
                </div>
                
                {/* Success Burst Animation */}
                {loadingPhase === 'complete' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-400 via-emerald-400 to-green-500 rounded-full flex items-center justify-center animate-ping">
                      <svg className="w-10 h-10 text-white animate-bounce" fill="none" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Brand Identity */}
            <div className="mb-6">
              <h1 className={`text-3xl font-black text-white mb-2 transform transition-all duration-700 ${
                loadingPhase === 'initializing' ? 'translate-y-6 opacity-0' : 'translate-y-0 opacity-100'
              }`}>
                <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-green-500 bg-clip-text text-transparent">
                  Yakjehti
                </span>{' '}
                <span className="text-white">Pakistan</span>
              </h1>
              <p className={`text-green-100 font-medium transform transition-all duration-700 delay-300 ${
                loadingPhase === 'initializing' ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-80'
              }`}>
                Welfare Society
              </p>
            </div>
            
            {/* Status Display */}
            <div className="mb-6">
              <div className={`inline-flex items-center px-4 py-2 rounded-full backdrop-blur-sm border transition-all duration-500 ${
                loadingPhase === 'complete' 
                  ? 'bg-green-500/20 border-green-400/50 text-green-300' 
                  : 'bg-white/10 border-white/20 text-white'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-3 ${
                  loadingPhase === 'complete' ? 'bg-green-400' : 'bg-blue-400 animate-pulse'
                }`}></div>
                <span className="font-semibold text-sm">
                  {loadingText}{loadingPhase !== 'complete' ? dots : ''}
                </span>
              </div>
            </div>
            
            {/* Advanced Progress System */}
            {loadingPhase === 'loading' && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-200 text-sm font-medium">Loading...</span>
                  <span className="text-green-300 text-sm font-bold">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden backdrop-blur-sm">
                  <div className="relative h-full">
                    <div 
                      className="bg-gradient-to-r from-green-400 via-emerald-400 to-green-500 h-full rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Sophisticated Loading Animation */}
            {loadingPhase !== 'complete' && (
              <div className="mb-6">
                <div className="flex justify-center items-center space-x-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`rounded-full transition-all duration-300 ${
                        i < 3 ? 'w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-400' : 'w-2 h-2 bg-green-300/60'
                      } animate-bounce`}
                      style={{ 
                        animationDelay: `${i * 0.15}s`,
                        animationDuration: '1s'
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Mission Statement */}
            <div className={`transform transition-all duration-700 delay-500 ${
              loadingPhase === 'initializing' ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
            }`}>
              <p className="text-green-100/80 text-sm leading-relaxed">
                Empowering communities across Pakistan through 
                <span className="text-green-300 font-semibold">welfare</span>, 
                <span className="text-emerald-300 font-semibold">development</span>, and 
                <span className="text-green-400 font-semibold">opportunity</span>.
              </p>
            </div>
          </div>
          
          {/* System Info */}
          <div className={`text-center mt-6 transition-all duration-700 delay-1000 ${
            loadingPhase === 'initializing' ? 'opacity-0' : 'opacity-60'
          }`}>
            <p className="text-green-200/60 text-xs font-medium">
              Version 2.0.0 • Enterprise Edition • 🇵🇰 Made for Pakistan
            </p>
          </div>
        </div>
      </div>
      
      {/* Advanced CSS Animations */}
      <style jsx>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(40px, 40px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.4); }
          50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.8); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AppLoader;
